import { getGoogleMapsApiKey } from "../config/googleMaps";
import { distanceService as defaultDistanceService } from "./distanceService";
import {
  GooglePlace,
  GooglePlaceWithDistance,
  GooglePlacesSearchParams,
} from "../types/googlePlaces";
import { IDistanceService } from "../types/services";

// Re-export types for backward compatibility
export type {
  GooglePlace,
  GooglePlaceWithDistance,
  GooglePlacesSearchParams,
};

// Global Window interface declaration for Google Maps
declare global {
  interface Window {
    google: any;
  }
}

class GooglePlacesService {
  private readonly apiKey: string;
  private placesService: google.maps.places.PlacesService | null = null;
  private placesServicePromise: Promise<google.maps.places.PlacesService> | null =
    null;

  constructor() {
    this.apiKey = getGoogleMapsApiKey();
  }

  // Initialize Places Service when Google Maps is loaded
  private async initializePlacesService(): Promise<google.maps.places.PlacesService> {
    if (this.placesService) {
      return this.placesService;
    }

    if (this.placesServicePromise) {
      return this.placesServicePromise;
    }

    this.placesServicePromise = new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait time

      const checkGoogleMaps = () => {
        attempts++;

        if (window.google && window.google.maps && window.google.maps.places) {
          // Create a dummy div for the PlacesService
          const dummyDiv = document.createElement("div");
          this.placesService = new window.google.maps.places.PlacesService(
            dummyDiv
          );
          resolve(this.placesService!);
        } else if (attempts >= maxAttempts) {
          reject(
            new Error("Google Maps Places API failed to load after 5 seconds")
          );
        } else {
          setTimeout(checkGoogleMaps, 100);
        }
      };
      checkGoogleMaps();
    });

    return this.placesServicePromise;
  }

  async searchNearbyPlaces(
    params: GooglePlacesSearchParams
  ): Promise<GooglePlace[]> {
    try {
      console.log("🔍 Searching Google Places using JavaScript API:", {
        location: params.location,
        radius: params.radius,
        keyword: params.keyword,
      });

      const placesService = await this.initializePlacesService();

      // Parse location
      const [lat, lng] = params.location
        .split(",")
        .map((coord) => parseFloat(coord.trim()));
      const location = new window.google.maps.LatLng(lat, lng);

      // Create request
      const request: google.maps.places.PlaceSearchRequest = {
        location: location,
        radius: params.radius,
        keyword: params.keyword,
        type: params.type || "establishment",
      };

      return new Promise((resolve, reject) => {
        placesService.nearbySearch(request, (results, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            results
          ) {
            console.log(
              `✅ Google Places API success: ${results.length} results found`
            );

            // Convert Google Maps results to our interface
            const places: GooglePlace[] = results.map((place: any) => ({
              place_id: place.place_id,
              name: place.name,
              vicinity: place.vicinity,
              rating: place.rating,
              user_ratings_total: place.user_ratings_total,
              types: place.types || [],
              geometry: {
                location: {
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng(),
                },
              },
              formatted_phone_number: place.formatted_phone_number,
              international_phone_number: place.international_phone_number,
              website: place.website,
              opening_hours: place.opening_hours
                ? {
                    open_now: place.opening_hours.open_now,
                    weekday_text: place.opening_hours.weekday_text,
                  }
                : undefined,
            }));

            resolve(places);
          } else if (
            status ===
            window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS
          ) {
            console.log("ℹ️ Google Places API: No results found");
            resolve([]);
          } else {
            console.error(`❌ Google Places API error: ${status}`);
            reject(new Error(`Google Places API error: ${status}`));
          }
        });
      });
    } catch (error) {
      console.error("❌ Error searching Google Places:", error);
      throw error;
    }
  }

  async getPlaceDetails(placeId: string): Promise<any> {
    try {
      console.log("🔍 Getting place details using JavaScript API:", {
        placeId,
      });

      const placesService = await this.initializePlacesService();

      const request: google.maps.places.PlaceDetailsRequest = {
        placeId: placeId,
        fields: [
          "formatted_phone_number",
          "international_phone_number",
          "website",
          "opening_hours",
          "formatted_address",
        ],
      };

      return new Promise((resolve, reject) => {
        placesService.getDetails(request, (place, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place
          ) {
            console.log("✅ Place details retrieved successfully");
            resolve({ result: place });
          } else {
            console.error(`❌ Error getting place details: ${status}`);
            reject(new Error(`Error getting place details: ${status}`));
          }
        });
      });
    } catch (error) {
      console.error("❌ Error getting place details:", error);
      throw error;
    }
  }

  // Fetch detailed information for a specific Google Place (lazy loading)
  async getPlaceDetailsForShop(placeId: string): Promise<{
    formatted_phone_number?: string;
    international_phone_number?: string;
    website?: string;
    formatted_address?: string;
    opening_hours?: {
      open_now: boolean;
      weekday_text?: string[];
    };
  }> {
    try {
      console.log(`🔍 Fetching details for place: ${placeId}`);
      const details = await this.getPlaceDetails(placeId);

      if (details.result) {
        console.log(`📞 Place details fetched:`, {
          place_id: placeId,
          hasPhone: !!details.result.formatted_phone_number,
          hasWebsite: !!details.result.website,
          hasHours: !!details.result.opening_hours,
          phone: details.result.formatted_phone_number,
          website: details.result.website,
          opening_hours: details.result.opening_hours,
        });

        return {
          formatted_phone_number: details.result.formatted_phone_number,
          international_phone_number: details.result.international_phone_number,
          website: details.result.website,
          formatted_address: details.result.formatted_address,
          opening_hours: details.result.opening_hours,
        };
      }

      return {};
    } catch (error) {
      console.error(`❌ Failed to get details for place ${placeId}:`, error);
      return {};
    }
  }

  // Get places sorted by aerial distance from a given location
  async getPlacesSortedByDistance(
    params: GooglePlacesSearchParams,
    originLat: number,
    originLon: number,
    distanceService: IDistanceService = defaultDistanceService // Dependency injection with default
  ): Promise<GooglePlaceWithDistance[]> {
    try {
      const places = await this.searchNearbyPlaces(params);

      // Convert to coordinate format for DistanceService
      const destinations = places.map((place) => ({
        id: place.place_id,
        latitude: place.geometry.location.lat,
        longitude: place.geometry.location.lng,
      }));

      const origin = { latitude: originLat, longitude: originLon };

      // Use injected DistanceService to get closest destinations by aerial distance
      const closestDestinations =
        distanceService.getClosestDestinationsByAerialDistance(
          origin,
          destinations,
          places.length // Get all places, sorted by distance
        );

      // Map back to GooglePlaceWithDistance format
      const placesWithDistance = closestDestinations.map((dest) => {
        const place = places.find((p) => p.place_id === dest.id)!;
        return {
          ...place,
          aerialDistance: dest.aerialDistance,
        };
      });

      // Return places with distance information
      // Note: Detailed information (phone, hours, website) will be fetched lazily when a place is selected
      console.log("📍 Google Places search completed:", {
        totalResults: placesWithDistance.length,
        note: "Detailed information will be fetched when places are selected",
      });

      return placesWithDistance;
    } catch (error) {
      console.error("❌ Error getting places sorted by distance:", error);
      throw error;
    }
  }
}

export const googlePlacesService = new GooglePlacesService();
